import { System, Entity, Not } from "ecsy";
import { Dead, Virus, OnDeath, Ownership, BloodCell, Targeting, OnSurvive, Sync, Infected, Spawner, Cooldown } from "../components";
import { createEntity, destroyAllEntities, destroyEntity, getId, popCreatedEntitiesBuffer, popDestroyedEntitiesBuffer, processEntityCreationAndDestructionBuffers } from "../entityFactory";
import { Game } from "../game";
import { Debug } from "../debug";
import { getEntityPlayer, instantiateComp } from "../composition";
import { triggerEffect } from "../triggeredEffects";
import { Multiplayer } from "../constants";
import { Player } from "../player";
import { count } from "console";

export class LifeAndDeathSystem extends System {
	game: Game;

	respawnInfectedCells: boolean;

	private queueWorldReset = false;
	private queueWorldResetIsSynced = false;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;

		this.respawnInfectedCells = false; // numPlayers > 1 && !Debug.NoInfectedCells //Infected cell feature canceled for now
	}

	execute() {
		//World reset this frame, nothing else matters
		if (this.queueWorldReset) {
			destroyAllEntities();

			this.game.players.forEach(player => {
				instantiateComp(this.game.world, player.hpCells, player.id, this.queueWorldResetIsSynced);
				instantiateComp(this.game.world, player.infectedCells, player.id, this.queueWorldResetIsSynced);
				instantiateComp(this.game.world, player.immuneCells, player.id, this.queueWorldResetIsSynced);
				instantiateComp(this.game.world, player.enemyCells, player.id, this.queueWorldResetIsSynced);
				for (const protein of player.droppedProteins) {
					createEntity({
						type: protein.type,
						position: protein.position,
						rotation: 0,
						id: protein.id,
						playerId: player.id,
						unsynced: this.queueWorldResetIsSynced,
					});
				}
			});

			//Do the actual killing, then the creating of new entities.
			processEntityCreationAndDestructionBuffers(this.world);

			//Don't transmit these resets in the sync system
			popCreatedEntitiesBuffer();
			popDestroyedEntitiesBuffer();

			this.queueWorldReset = false;
			this.queueWorldResetIsSynced = false;

			if (this.game.inSimulation) {
				this.game.numberOfViruses = this.countRoundRemainingViruses();
			}
			return;
		}

		//Normal frame
		const diesThisFrame = new Array<Entity>();
		const cancelDeathList = new Array<Entity>();
		this.queries.dead.results.forEach(entity => {
			if (entity.hasComponent(BloodCell) && entity.hasComponent(Ownership)) {
				const owner = entity.getComponent(Ownership);
				const player = getEntityPlayer(entity, this.game);
				if (player) {
					player.hp -= 1;
					if (owner.sourceCompElement) {
						//Make the blood cell as dead so it will remain submerged in future rounds
						if (!owner.sourceCompElement.entityParameters) {
							owner.sourceCompElement.entityParameters = {};
						}
						owner.sourceCompElement.entityParameters.wasKilled = true;

						//If infected cells are in play create one here
						if (process.env.is_server && this.respawnInfectedCells) {
							const death = entity.getComponent(Dead);
							const type = death.killerType;
							this.game.createInfectedCell(player, type);
						}
					}

					//Keep track of lost cells for the end of the round
					const lost = this.game.lostCellsPerPlayerThisRound.get(player.id) || 0;
					this.game.lostCellsPerPlayerThisRound.set(player.id, lost + 1)
					console.log(`player ${player.id} lost ${this.game.lostCellsPerPlayerThisRound.get(player.id)} cells`);
				}
				--this.game.numberOfBloodCells;
				//Blood cells submerge instead of being destoryed, so remove the Dead component once this is processed
				cancelDeathList.push(entity);
			}
			else {
				diesThisFrame.push(entity);
			}
		})
		cancelDeathList.forEach(entity => entity.removeComponent(Dead));

		for (const corpse of diesThisFrame) {
			destroyEntity(corpse);
		}

		//Do the actual killing, then the creating of new entities.
		processEntityCreationAndDestructionBuffers(this.world);

		if (this.game.getCurrentState() === 'shop') {
			while (this.queries.survivors.results.length > 0) {
				const entity = this.queries.survivors.results[0];
				const trigger = entity.getComponent(OnSurvive);
				const id = getId(entity);
				if (id !== null && this.game.lastRoundSurvivors.includes(id)) {
					triggerEffect(trigger.effect, entity);
				}
				entity.removeComponent(OnSurvive);
			}
		}

		if (this.game.inSimulation) {
			this.game.numberOfViruses = this.countRoundRemainingViruses();
		}
	}

	/**Should happen at start of shop and start of simulation */
	queueResetWorldToComposition(unsynced = false) {
		this.queueWorldReset = true;
		this.queueWorldResetIsSynced = unsynced;
	}

	countRoundRemainingViruses() {
		let count = this.queries.viruses.results.length;
		for (const entity of this.queries.virusSpawners.results) {
        const spawner = entity.getComponent(Spawner);
        const cooldown = entity.getComponent(Cooldown);
        // Only count cells that have been activated by nextVirusWave (extra != Infinity)
        if (spawner.amount > 0 && cooldown.extra !== Infinity) {
            count += spawner.amount;
        }
    }
		return count;
	}
}

LifeAndDeathSystem.queries = {
	dead: { components: [Dead] },
	targeting: { components: [Targeting] },
	survivors: { components: [OnSurvive], listen: { added: true } },
	viruses: { components: [Virus] },
	virusSpawners: { components: [Spawner, Infected] },
};
